import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  createCasaMapaListingOperatorFlag,
  fetchCasaMapaPublicInventoryListingDetail,
  resolveCasaMapaListingOperatorFlag,
} from "../../../api/clientCasamapaAdminApi";
import CasaMapaAdminListingAddress from "../../../components/casamapa/admin/CasaMapaAdminListingAddress";
import { useCurrency, useLocale } from "../../../intl";
import "../../../styles/casamapaListingAdminDetail.css";

const COPY = "realEstate.casaMapa.admin.publicInventory";

const FLAG_OPTIONS = [
  "duplicate",
  "bad_price",
  "wrong_location",
  "missing_photos",
  "incomplete_data",
  "wrong_agency",
  "other",
];

function Fact({ label, children }) {
  if (children == null || children === "") return null;
  return (
    <div className="cm-trend-modal__fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export default function CasaMapaPublicInventoryDetailModal({
  clientViewId,
  listingId,
  path,
  onClose,
  onFlagsChanged,
}) {
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const titleId = useId();
  const closeButtonRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [flagType, setFlagType] = useState("duplicate");
  const [flagNotes, setFlagNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!clientViewId || !listingId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchCasaMapaPublicInventoryListingDetail(clientViewId, listingId)
      .then((result) => {
        if (!cancelled) setPayload(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t(`${COPY}.modal.loadError`));
          setPayload(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId, listingId, t]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const listing = payload?.detail || payload?.listing || null;
  const flags = payload?.flags || [];
  const openFlags = flags.filter((flag) => flag.status === "open");
  const photo =
    listing?.photoUrl ||
    listing?.images?.[0]?.url ||
    payload?.inventoryListing?.photoUrl ||
    null;
  const place = [listing?.neighborhood, listing?.municipality].filter(Boolean).join(", ");

  const refreshFlags = (nextFlags) => {
    setPayload((current) => (current ? { ...current, flags: nextFlags } : current));
    onFlagsChanged?.(listingId, nextFlags.filter((flag) => flag.status === "open"));
  };

  const submitFlag = async (event) => {
    event.preventDefault();
    if (!clientViewId || !listingId) return;
    setBusy(true);
    setError("");
    try {
      const result = await createCasaMapaListingOperatorFlag(clientViewId, listingId, {
        flagType,
        notes: flagNotes.trim() || undefined,
      });
      refreshFlags(result.flags || []);
      setFlagNotes("");
    } catch (err) {
      setError(err.message || t(`${COPY}.modal.flagError`));
    } finally {
      setBusy(false);
    }
  };

  const clearFlag = async (flag) => {
    if (!clientViewId || !listingId) return;
    setBusy(true);
    setError("");
    try {
      const result = await resolveCasaMapaListingOperatorFlag(clientViewId, listingId, flag.flagId, {
        status: "resolved",
      });
      refreshFlags(result.flags || []);
    } catch (err) {
      setError(err.message || t(`${COPY}.modal.flagError`));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="cm-trend-modal-root">
      <div className="cm-trend-modal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="cm-trend-modal cm-pub-inv-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(event) => event.stopPropagation()}
        >
          <header className="cm-trend-modal__header">
            <div className="cm-trend-modal__intro">
              {photo ? <img className="cm-trend-modal__photo" src={photo} alt="" /> : null}
              <div>
                <p className="cm-trend-modal__eyebrow">{t(`${COPY}.modal.eyebrow`)}</p>
                <h2 id={titleId}>{listing?.title || t(`${COPY}.untitled`)}</h2>
                <p className="muted">{place || "—"}</p>
                <nav className="cm-trend-modal__links" aria-label={t(`${COPY}.modal.linksAria`)}>
                  {listing?.publicUrl ? (
                    <a href={listing.publicUrl} target="_blank" rel="noopener noreferrer">
                      {t(`${COPY}.modal.viewPublic`)}
                    </a>
                  ) : null}
                  <Link to={path(`listings/${listingId}`)}>{t(`${COPY}.modal.viewAdmin`)}</Link>
                  <Link to={path("property-linking")}>{t(`${COPY}.modal.openLinking`)}</Link>
                </nav>
              </div>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              className="cm-trend-modal__close"
              onClick={onClose}
            >
              {t(`${COPY}.modal.close`)}
            </button>
          </header>

          <div className="cm-trend-modal__body">
            {loading ? <p className="muted">{t(`${COPY}.modal.loading`)}</p> : null}
            {error ? <p className="form-error">{error}</p> : null}

            {!loading && listing ? (
              <>
                <CasaMapaAdminListingAddress mailingAddress={listing.mailingAddress} t={t} />

                <dl className="cm-trend-modal__facts">
                  <Fact label={t(`${COPY}.agency`)}>
                    {listing.agencyName || listing.sourceKey || "—"}
                  </Fact>
                  <Fact label={t(`${COPY}.price`)}>
                    {listing.priceCop != null
                      ? formatMoney(listing.priceCop, { storageCurrency: "COP" })
                      : "—"}
                  </Fact>
                  <Fact label={t(`${COPY}.modal.bedrooms`)}>{listing.bedrooms}</Fact>
                  <Fact label={t(`${COPY}.modal.bathrooms`)}>{listing.bathrooms}</Fact>
                  <Fact label={t(`${COPY}.modal.area`)}>
                    {listing.areaSqm != null ? `${listing.areaSqm} m²` : null}
                  </Fact>
                  <Fact label={t(`${COPY}.modal.propertyType`)}>{listing.propertyType}</Fact>
                  <Fact label={t(`${COPY}.listingType`)}>{listing.listingType}</Fact>
                  <Fact label={t(`${COPY}.status`)}>{listing.status}</Fact>
                </dl>

                {listing.description ? (
                  <section className="cm-trend-modal__section">
                    <h3>{t(`${COPY}.modal.description`)}</h3>
                    <p className="cm-pub-inv-modal__description">{listing.description}</p>
                  </section>
                ) : null}

                {listing.images?.length > 1 ? (
                  <section className="cm-trend-modal__section">
                    <h3>{t(`${COPY}.modal.photos`)}</h3>
                    <div className="cm-pub-inv-modal__gallery">
                      {listing.images.slice(0, 8).map((image) => (
                        <img key={image.id || image.url} src={image.thumbnailUrl || image.url} alt="" />
                      ))}
                    </div>
                  </section>
                ) : null}

                <section className="cm-trend-modal__section">
                  <h3>{t(`${COPY}.modal.flagsTitle`)}</h3>
                  <p className="muted">{t(`${COPY}.modal.flagsHint`)}</p>

                  {openFlags.length ? (
                    <ul className="cm-pub-inv-modal__flag-list">
                      {openFlags.map((flag) => (
                        <li key={flag.flagId}>
                          <div>
                            <strong>{t(`${COPY}.flagTypes.${flag.flagType}`, {}, flag.flagType)}</strong>
                            {flag.notes ? <small>{flag.notes}</small> : null}
                          </div>
                          <button
                            type="button"
                            className="casamapa-admin-link-button"
                            disabled={busy}
                            onClick={() => clearFlag(flag)}
                          >
                            {t(`${COPY}.modal.resolveFlag`)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted">{t(`${COPY}.modal.noFlags`)}</p>
                  )}

                  <form className="cm-pub-inv-modal__flag-form" onSubmit={submitFlag}>
                    <label>
                      <span>{t(`${COPY}.modal.flagType`)}</span>
                      <select value={flagType} onChange={(event) => setFlagType(event.target.value)}>
                        {FLAG_OPTIONS.map((type) => (
                          <option key={type} value={type}>
                            {t(`${COPY}.flagTypes.${type}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>{t(`${COPY}.modal.flagNotes`)}</span>
                      <textarea
                        value={flagNotes}
                        onChange={(event) => setFlagNotes(event.target.value)}
                        rows={3}
                        placeholder={
                          flagType === "duplicate"
                            ? t(`${COPY}.modal.duplicateNotesPlaceholder`)
                            : t(`${COPY}.modal.notesPlaceholder`)
                        }
                      />
                    </label>
                    <button type="submit" className="midpoint-share-button" disabled={busy}>
                      {busy ? t(`${COPY}.modal.saving`) : t(`${COPY}.modal.addFlag`)}
                    </button>
                  </form>
                </section>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
